/**
 * 📂 File: src/app/student-animation-showcase/helpers/dates.ts
 * 🧭 Description:
 * Date parsing and formatting helpers for the Student Animation Showcase.
 */

/* -------------------------------------------------- */
/* 📅 Parse SAS Date                                  */
/* -------------------------------------------------- */

export function parseDate(
  date?: string | null,
  time?: string | null,
  timezone?: string | null,
): Date | null {
  if (!date) return null;

  // Extract YYYY-MM-DD
  const [year, month, day] = date.split('-').map(Number);

  if (!year || !month || !day) return null;

  let hours = 0;
  let minutes = 0;

  if (time) {
    const match = time
      .trim()
      .toLowerCase()
      .match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/);

    if (match) {
      let h = Number(match[1]);
      const m = Number(match[2] || 0);

      if (match[3] === 'pm' && h !== 12) h += 12;
      if (match[3] === 'am' && h === 12) h = 0;

      hours = h;
      minutes = m;
    }
  }

  // PDT is UTC-7 during summer
  if (timezone === 'PDT') {
    return new Date(Date.UTC(year, month - 1, day, hours + 7, minutes));
  }

  return new Date(year, month - 1, day, hours, minutes);
}

/* -------------------------------------------------- */
/* 📅 Pretty Date                                     */
/* -------------------------------------------------- */

export function formatPrettyDate(date: Date | null): string {
  if (!date) return '';

  const day = date.getDate();
  const year = date.getFullYear();
  const month = date.toLocaleString('en-US', { month: 'long' });

  const suffix =
    day % 10 === 1 && day !== 11
      ? 'st'
      : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
          ? 'rd'
          : 'th';

  return `${month} ${day}${suffix}, ${year}`;
}

/* -------------------------------------------------- */
/* 🕒 Pretty Time                                     */
/* -------------------------------------------------- */

export function formatPrettyTime(time?: string | null): string {
  if (!time) return '';

  const normalized = time
    .trim()
    .toLowerCase()
    .replace(/(\d)(am|pm)/, '$1 $2');

  const parsed = new Date(`1970-01-01 ${normalized}`);

  if (Number.isNaN(parsed.getTime())) {
    return time;
  }

  return parsed.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
